"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.markTestAnswerSchema = exports.GRADER_NOTE_MAX = exports.GRADING_OVERDUE_HOURS = exports.GRADING_REMINDER_HOURS = exports.saveTestAnswerSchema = exports.TEST_TOKEN_HEADER = exports.sendTestSchema = exports.TEST_ACTION_LABELS = exports.FINISHED_ATTEMPT_STATUSES = exports.OPEN_ATTEMPT_STATUSES = exports.ATTEMPT_STATUS_LABELS = exports.assessmentListQuerySchema = exports.createAssessmentSchema = exports.createQuestionSchema = exports.QUESTION_FACET_KEYS = exports.questionFacetsQuerySchema = exports.questionListQuerySchema = exports.ASSESSMENT_FILE_RULE = exports.ASSESSMENT_FILE_ACCEPT = exports.ASSESSMENT_FILE_EXTENSIONS = exports.ASSESSMENT_FILE_MAX_BYTES = exports.ASSESSMENT_REMINDER_HOURS = exports.ANSWER_GRACE_SECONDS = exports.testAnswerSchema = exports.updateAssessmentSchema = exports.assessmentInputSchema = exports.DEFAULT_TEST_PASS_PERCENT = exports.SECTION_MINUTES_MAX = exports.SECTION_QUESTIONS_MAX = exports.ASSESSMENT_SECTIONS_MAX = exports.ASSESSMENT_NAME_MAX = exports.updateQuestionSchema = exports.questionInputSchema = exports.ACCEPTED_ANSWER_MAX = exports.ACCEPTED_ANSWERS_MAX = exports.QUESTION_TAG_MAX = exports.QUESTION_TAGS_MAX = exports.QUESTION_POINTS_MAX = exports.QUESTION_OPTION_TEXT_MAX = exports.QUESTION_OPTIONS_MAX = exports.QUESTION_OPTIONS_MIN = exports.QUESTION_TEXT_MAX = exports.QUESTION_DIFFICULTY_LABELS = exports.QUESTION_DIFFICULTIES = exports.ASSESSMENT_QUESTION_TYPE_LABELS = exports.ASSESSMENT_QUESTION_TYPES = void 0;
exports.isChoiceQuestion = isChoiceQuestion;
exports.questionProblems = questionProblems;
exports.isAutoMarked = isAutoMarked;
exports.drawMarksProblem = drawMarksProblem;
exports.testTotals = testTotals;
exports.shuffled = shuffled;
exports.drawPaper = drawPaper;
exports.normaliseShortAnswer = normaliseShortAnswer;
exports.markAnswer = markAnswer;
exports.attemptOutcome = attemptOutcome;
exports.linkExpiresAt = linkExpiresAt;
exports.sectionDeadline = sectionDeadline;
exports.secondsLeft = secondsLeft;
exports.isSectionOver = isSectionOver;
exports.clockSteps = clockSteps;
exports.answersAcceptedUntil = answersAcceptedUntil;
exports.reminderDue = reminderDue;
exports.attemptStatusLabel = attemptStatusLabel;
exports.nextTestAction = nextTestAction;
exports.gradingChasersDue = gradingChasersDue;
exports.markProblem = markProblem;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// S17 — the question bank (ASM-01), the test builder (ASM-02), the candidate's test
// (ASM-03) and results (ASM-07). Sources: master plan S17 and its planning decisions
// (DECISIONS-LOG S17 Q1–Q8); F5.08, F9.01–F9.07, F9.15, F9.16; E7; D11; D16; §L6; §L9;
// §L19; §L22 N-20–N-23; ADR 0019 for what the documents left open.
// --- The bank ------------------------------------------------------------------------
/** E7's four kinds, stored in the S1 `question_type` enum ("MCQ" is one answer or several). */
exports.ASSESSMENT_QUESTION_TYPES = [
    'single_select',
    'multi_select',
    'text',
    'long_text',
    'file',
];
exports.ASSESSMENT_QUESTION_TYPE_LABELS = {
    single_select: 'Multiple choice — one answer',
    multi_select: 'Multiple choice — all that apply',
    text: 'Short answer',
    long_text: 'Long answer',
    file: 'File upload',
};
const CHOICE_TYPES = ['single_select', 'multi_select'];
function isChoiceQuestion(type) {
    return CHOICE_TYPES.includes(type);
}
exports.QUESTION_DIFFICULTIES = ['easy', 'medium', 'hard'];
exports.QUESTION_DIFFICULTY_LABELS = {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard',
};
exports.QUESTION_TEXT_MAX = 4000;
exports.QUESTION_OPTIONS_MIN = 2;
exports.QUESTION_OPTIONS_MAX = 8;
exports.QUESTION_OPTION_TEXT_MAX = 500;
exports.QUESTION_POINTS_MAX = 100;
exports.QUESTION_TAGS_MAX = 10;
exports.QUESTION_TAG_MAX = 40;
exports.ACCEPTED_ANSWERS_MAX = 10;
exports.ACCEPTED_ANSWER_MAX = 200;
const questionOptionSchema = zod_1.z.object({
    text: zod_1.z.string().trim().min(1, 'This field is required.').max(exports.QUESTION_OPTION_TEXT_MAX),
    isCorrect: zod_1.z.boolean(),
});
/** F9.01. One shape for create and edit; an edit is version N+1 (ADR 0019). */
exports.questionInputSchema = zod_1.z
    .object({
    questionText: zod_1.z.string().trim().min(1, 'This field is required.').max(exports.QUESTION_TEXT_MAX),
    type: zod_1.z.enum(exports.ASSESSMENT_QUESTION_TYPES),
    points: zod_1.z.number().int('Whole marks only.').min(1, 'At least 1 mark.').max(exports.QUESTION_POINTS_MAX),
    difficulty: zod_1.z.enum(exports.QUESTION_DIFFICULTIES).nullable(),
    roleFamily: zod_1.z.string().trim().max(80).nullable(),
    tags: zod_1.z.array(zod_1.z.string().trim().min(1).max(exports.QUESTION_TAG_MAX)).max(exports.QUESTION_TAGS_MAX),
    options: zod_1.z.array(questionOptionSchema).max(exports.QUESTION_OPTIONS_MAX),
    /** S17 Q2: only a short answer takes these. */
    acceptedAnswers: zod_1.z.array(zod_1.z.string().trim().min(1).max(exports.ACCEPTED_ANSWER_MAX)).max(exports.ACCEPTED_ANSWERS_MAX),
    explanation: zod_1.z.string().trim().max(2000).nullable(),
})
    .superRefine((input, context) => {
    for (const problem of questionProblems(input)) {
        context.addIssue({ code: 'custom', path: [problem.field], message: problem.message });
    }
});
exports.updateQuestionSchema = schemas_1.versionedSchema.extend({ question: exports.questionInputSchema });
/** What makes a question unusable, in the words shown beside the field. */
function questionProblems(input) {
    const problems = [];
    const correct = input.options.filter((option) => option.isCorrect).length;
    if (isChoiceQuestion(input.type)) {
        if (input.options.length < exports.QUESTION_OPTIONS_MIN)
            problems.push({ field: 'options', message: `Give at least ${exports.QUESTION_OPTIONS_MIN} choices.` });
        if (input.type === 'single_select' && correct !== 1)
            problems.push({ field: 'options', message: 'Mark exactly one choice as correct.' });
        if (input.type === 'multi_select' && correct < 1)
            problems.push({ field: 'options', message: 'Mark at least one choice as correct.' });
    }
    else if (input.options.length > 0) {
        problems.push({ field: 'options', message: 'Only multiple-choice questions have choices.' });
    }
    if (input.type !== 'text' && input.acceptedAnswers.length > 0) {
        problems.push({ field: 'acceptedAnswers', message: 'Only short answers take a list of accepted answers.' });
    }
    return problems;
}
/**
 * Whether the system marks this question by itself (S17 Q2). Multiple choice always;
 * a short answer when accepted answers are listed; long answers and files never.
 */
function isAutoMarked(type, acceptedAnswers) {
    if (isChoiceQuestion(type))
        return true;
    return type === 'text' && acceptedAnswers.length > 0;
}
// --- The builder ---------------------------------------------------------------------
exports.ASSESSMENT_NAME_MAX = 120;
exports.ASSESSMENT_SECTIONS_MAX = 10;
exports.SECTION_QUESTIONS_MAX = 200;
exports.SECTION_MINUTES_MAX = 180;
/** A new test's suggested pass mark — the job's own default (S12 Q10). */
exports.DEFAULT_TEST_PASS_PERCENT = 60;
const sectionInputSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'This field is required.').max(120),
    instructions: zod_1.z.string().trim().max(2000).nullable(),
    durationMinutes: zod_1.z.number().int('Whole minutes only.').min(1, 'At least 1 minute.').max(exports.SECTION_MINUTES_MAX),
    /** S17 Q1: null = every question in the pool. */
    drawCount: zod_1.z.number().int('Whole numbers only.').min(1, 'At least 1.').nullable(),
    /** Question families; the save pins their current versions (ADR 0019). */
    questionFamilyIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Add at least one question.').max(exports.SECTION_QUESTIONS_MAX),
});
/** F9.02. One shape for create and edit; an edit is version N+1. */
exports.assessmentInputSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1, 'This field is required.').max(exports.ASSESSMENT_NAME_MAX),
    description: zod_1.z.string().trim().max(2000).nullable(),
    passPercent: zod_1.z.number().int('Whole numbers only.').min(1).max(100),
    isShuffled: zod_1.z.boolean(),
    /** S18 Q1: sat on paper at an exam session, not sent as a link. */
    isOffline: zod_1.z.boolean().default(false),
    sections: zod_1.z.array(sectionInputSchema).min(1, 'Add at least one section.').max(exports.ASSESSMENT_SECTIONS_MAX),
})
    .superRefine((input, context) => {
    input.sections.forEach((section, index) => {
        if (new Set(section.questionFamilyIds).size !== section.questionFamilyIds.length) {
            context.addIssue({ code: 'custom', path: ['sections', index, 'questionFamilyIds'], message: 'A question is in this section twice.' });
        }
        if (section.drawCount !== null && section.drawCount > section.questionFamilyIds.length) {
            context.addIssue({
                code: 'custom',
                path: ['sections', index, 'drawCount'],
                message: `The section has ${section.questionFamilyIds.length} questions, so it can give at most ${section.questionFamilyIds.length}.`,
            });
        }
    });
});
exports.updateAssessmentSchema = schemas_1.versionedSchema.extend({ assessment: exports.assessmentInputSchema });
/**
 * A section that draws questions at random must give every candidate a paper worth the
 * same, so its questions must all carry the same marks (ADR 0019). Null = fine.
 */
function drawMarksProblem(drawCount, points) {
    if (drawCount === null || drawCount >= points.length)
        return null;
    const first = points[0];
    if (points.every((value) => value === first))
        return null;
    return 'Questions given at random must all carry the same marks, so every paper is worth the same.';
}
/** A test's total marks and minutes, as the builder shows them and the version stores them. */
function testTotals(sections) {
    let totalPoints = 0;
    let durationMinutes = 0;
    for (const section of sections) {
        durationMinutes += section.durationMinutes;
        const given = section.drawCount === null ? section.points.length : Math.min(section.drawCount, section.points.length);
        // Equal marks are enforced when drawing, so the first `given` marks are representative.
        totalPoints += section.points.slice(0, given).reduce((sum, value) => sum + value, 0);
    }
    return { totalPoints, durationMinutes };
}
/** Fisher–Yates on a copy. `random` returns [0, 1), as `Math.random` does. */
function shuffled(items, random) {
    const copy = [...items];
    for (let index = copy.length - 1; index > 0; index -= 1) {
        const other = Math.floor(random() * (index + 1));
        const held = copy[index];
        copy[index] = copy[other];
        copy[other] = held;
    }
    return copy;
}
/**
 * One candidate's paper. A section giving fewer than its pool always picks at random —
 * that is what drawing is. Order and answer choices shuffle when the test says so;
 * otherwise the drawn questions keep the builder's order.
 */
function drawPaper(sections, optionIds, isShuffled, random) {
    return sections.map((section) => {
        const count = section.drawCount === null ? section.questionIds.length : Math.min(section.drawCount, section.questionIds.length);
        const picked = count < section.questionIds.length ? new Set(shuffled(section.questionIds, random).slice(0, count)) : new Set(section.questionIds);
        const inBuilderOrder = section.questionIds.filter((id) => picked.has(id));
        const ordered = isShuffled ? shuffled(inBuilderOrder, random) : inBuilderOrder;
        return {
            sectionId: section.sectionId,
            questions: ordered.map((questionId) => {
                const options = optionIds.get(questionId) ?? null;
                return { questionId, optionOrder: options === null ? null : isShuffled ? shuffled(options, random) : [...options] };
            }),
        };
    });
}
// --- Marking --------------------------------------------------------------------------
/** What a candidate sent for one question. */
exports.testAnswerSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('choice'), optionIds: zod_1.z.array(schemas_1.uuidSchema).max(exports.QUESTION_OPTIONS_MAX) }),
    zod_1.z.object({ kind: zod_1.z.literal('text'), text: zod_1.z.string().max(20_000) }),
]);
/**
 * Short answers compare loosely: case, surrounding space, doubled spaces and a closing
 * full stop never make a right answer wrong ("300", " 300. " and "300" all match).
 */
function normaliseShortAnswer(text) {
    return text.normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.。।]+$/u, '').trim();
}
/**
 * F9.05. Multiple choice scores full marks only when exactly right — "all that apply"
 * included (no part marks). A blank answer scores nothing and needs nobody, even on a
 * question a person would otherwise mark. `hasFile` says whether a file was uploaded.
 */
function markAnswer(question, answer, hasFile = false) {
    const wrong = { points: 0, isCorrect: false, needsGrader: false };
    if (question.type === 'file') {
        return hasFile ? { points: null, isCorrect: null, needsGrader: true } : wrong;
    }
    if (answer === null)
        return wrong;
    if (isChoiceQuestion(question.type)) {
        if (answer.kind !== 'choice')
            return wrong;
        const chosen = new Set(answer.optionIds);
        const isRight = chosen.size === question.correctOptionIds.length && question.correctOptionIds.every((id) => chosen.has(id));
        return isRight ? { points: question.points, isCorrect: true, needsGrader: false } : wrong;
    }
    if (answer.kind !== 'text' || answer.text.trim() === '')
        return wrong;
    if (question.type === 'long_text' || question.acceptedAnswers.length === 0) {
        return { points: null, isCorrect: null, needsGrader: true };
    }
    const given = normaliseShortAnswer(answer.text);
    const isRight = question.acceptedAnswers.some((accepted) => normaliseShortAnswer(accepted) === given);
    return isRight ? { points: question.points, isCorrect: true, needsGrader: false } : wrong;
}
/** The attempt's score from its marks, judged against the pass mark it was sent with. */
function attemptOutcome(marks, passPercent) {
    const scoreMax = marks.reduce((sum, entry) => sum + entry.maxPoints, 0);
    const scoreRaw = marks.reduce((sum, entry) => sum + (entry.mark.points ?? 0), 0);
    const hasPendingManualGrading = marks.some((entry) => entry.mark.needsGrader && entry.mark.points === null);
    const scorePercent = scoreMax === 0 ? 0 : Math.round((scoreRaw / scoreMax) * 10_000) / 100;
    // Compared on the raw figures, so rounding to two places can never flip a result.
    const didPass = hasPendingManualGrading ? null : scoreRaw * 100 >= passPercent * scoreMax;
    return { scoreRaw, scoreMax, scorePercent, hasPendingManualGrading, didPass };
}
// --- The clock ------------------------------------------------------------------------
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
/**
 * A save that leaves the phone just before a section closes still counts if it lands
 * within this. The candidate's screen stops accepting answers at zero; this only covers
 * the time the last save spends on the network.
 */
exports.ANSWER_GRACE_SECONDS = 15;
/** A link lasts whole days of wall-clock time from when it is sent (F5.08). */
function linkExpiresAt(sentAt, days) {
    return new Date(sentAt.getTime() + days * 24 * HOUR_MS);
}
function sectionDeadline(startedAt, durationMinutes) {
    return new Date(startedAt.getTime() + durationMinutes * MINUTE_MS);
}
function secondsLeft(deadline, now) {
    return Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / 1000));
}
/** True once a section's time — and the grace for a save in flight — is over. */
function isSectionOver(deadline, now, graceSeconds = exports.ANSWER_GRACE_SECONDS) {
    return now.getTime() > deadline.getTime() + graceSeconds * 1000;
}
/**
 * Where a running test's clock has got to (ADR 0019). Sections run back to back: one
 * closes when its time is up (or when the candidate moves on early — the caller closes
 * it first), and the next starts at that moment, whether or not anyone is watching. So
 * a candidate who drops out and returns finds the clock where it would have been (S17
 * Q7). Returns the opens and closes to write, the section now open, and whether every
 * section is over. A test not yet started (its first section unopened) is left alone.
 */
function clockSteps(sections, now, graceSeconds = exports.ANSWER_GRACE_SECONDS) {
    const steps = [];
    let startAt = null;
    for (const [index, section] of sections.entries()) {
        if (section.closedAt) {
            startAt = section.closedAt;
            continue;
        }
        let deadline = section.deadlineAt;
        if (!section.startedAt || !deadline) {
            if (startAt === null)
                return { steps, current: null, isFinished: false };
            deadline = sectionDeadline(startAt, section.durationMinutes);
            steps.push({ kind: 'open', index, startedAt: startAt, deadlineAt: deadline });
        }
        if (!isSectionOver(deadline, now, graceSeconds))
            return { steps, current: index, isFinished: false };
        steps.push({ kind: 'close', index, closedAt: deadline });
        startAt = deadline;
    }
    return { steps, current: null, isFinished: true };
}
/**
 * When a section stops taking answers. A section closed by its clock takes a save still
 * in flight for the grace; one the candidate left early does not — the page finishes
 * saving before it moves on, and a grace there would let a candidate go back and change
 * answers after seeing the next section.
 */
function answersAcceptedUntil(section, graceSeconds = exports.ANSWER_GRACE_SECONDS) {
    if (!section.deadlineAt)
        return null;
    if (section.closedAt && section.closedAt.getTime() < section.deadlineAt.getTime())
        return section.closedAt;
    return new Date(section.deadlineAt.getTime() + graceSeconds * 1000);
}
/** §L22 N-21: T-48h and T-12h before the link expires. */
exports.ASSESSMENT_REMINDER_HOURS = [48, 12];
/**
 * Whether a reminder should go now, and the count to store after this sweep. A reminder
 * whose moment came before the link was even sent is skipped rather than sent late (a
 * 1-day link gets only the 12-hour one), and two that fell due together go as one.
 */
function reminderDue(input) {
    let count = input.remindersSent;
    let isDue = false;
    if (input.now.getTime() >= input.expiresAt.getTime())
        return { isDue, remindersSent: count };
    for (let index = count; index < exports.ASSESSMENT_REMINDER_HOURS.length; index += 1) {
        const moment = input.expiresAt.getTime() - (exports.ASSESSMENT_REMINDER_HOURS[index] ?? 0) * HOUR_MS;
        if (moment <= input.sentAt.getTime()) {
            count = index + 1;
            continue;
        }
        if (moment > input.now.getTime())
            break;
        count = index + 1;
        isDue = true;
    }
    return { isDue, remindersSent: count };
}
// --- The candidate's upload -----------------------------------------------------------
/** The `documents` cap, stated in the error (§L26 "Maximum 10 MB"). */
exports.ASSESSMENT_FILE_MAX_BYTES = 10 * 1024 * 1024;
/** ADR 0019: what a work-sample task is usually handed back as. Checked by content on the server. */
exports.ASSESSMENT_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png', 'zip'];
exports.ASSESSMENT_FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.zip';
exports.ASSESSMENT_FILE_RULE = 'PDF, Word, Excel, CSV, JPG, PNG or ZIP. Maximum 10 MB.';
// --- What the bank and the builder read (ASM-01, ASM-02) ------------------------------
exports.questionListQuerySchema = zod_1.z.object({
    /** 06-qol §6: type-ahead from two characters. */
    query: zod_1.z.string().trim().min(2).max(200).optional(),
    type: zod_1.z.enum(exports.ASSESSMENT_QUESTION_TYPES).optional(),
    difficulty: zod_1.z.enum(exports.QUESTION_DIFFICULTIES).optional(),
    tag: zod_1.z.string().trim().min(1).max(exports.QUESTION_TAG_MAX).optional(),
    roleFamily: zod_1.z.string().trim().min(1).max(80).optional(),
    ventureId: schemas_1.uuidSchema.optional(),
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
/** The bank's filters, without paging: what the facet counts are taken against. */
exports.questionFacetsQuerySchema = exports.questionListQuerySchema.omit({ page: true, pageSize: true });
exports.QUESTION_FACET_KEYS = ['type', 'difficulty', 'tag', 'roleFamily'];
exports.createQuestionSchema = zod_1.z.object({ ventureId: schemas_1.uuidSchema, question: exports.questionInputSchema });
exports.createAssessmentSchema = zod_1.z.object({ ventureId: schemas_1.uuidSchema, assessment: exports.assessmentInputSchema });
exports.assessmentListQuerySchema = zod_1.z.object({
    query: zod_1.z.string().trim().min(2).max(200).optional(),
    ventureId: schemas_1.uuidSchema.optional(),
});
// --- Sending a test, and what the recruiter sees (F9.03, ASM-07) -----------------------
exports.ATTEMPT_STATUS_LABELS = {
    not_started: 'Link sent',
    in_progress: 'Taking it now',
    submitted: 'Waiting for marking',
    auto_graded: 'Marked',
    manually_graded: 'Marked',
    expired: 'Expired unopened',
    // Only an exam session writes it: a person marked absent (E7 — its own status, never a fail).
    abandoned: 'No-show',
    cancelled: 'Withdrawn',
};
/** The status as the recruiter reads it; a booked exam seat is not a sent link. */
function attemptStatusLabel(status, isOffline) {
    if (!isOffline)
        return exports.ATTEMPT_STATUS_LABELS[status];
    if (status === 'not_started')
        return 'Booked for an exam';
    if (status === 'cancelled')
        return 'Booking withdrawn';
    return exports.ATTEMPT_STATUS_LABELS[status];
}
/** The link still works: not submitted, not expired, not withdrawn. */
exports.OPEN_ATTEMPT_STATUSES = ['not_started', 'in_progress'];
/** Taken to the end — these, and only these, use up one of the job's attempts (ADR 0019). */
exports.FINISHED_ATTEMPT_STATUSES = ['submitted', 'auto_graded', 'manually_graded'];
exports.TEST_ACTION_LABELS = {
    send: 'Send test',
    resend: 'Send a new link',
    retake: 'Send a retake',
    book: 'Book a session',
};
/** Ended without a result the candidate earned: expired, withdrawn, or a no-show. */
const UNSAT_STATUSES = ['expired', 'cancelled', 'abandoned'];
/**
 * What the recruiter may do for one applicant now. Only in the Assessment stage. A link
 * that expired or was withdrawn unopened can be sent again without using an attempt; a
 * retake is offered after a fail, while attempts remain (S17 Q8). Never automatic.
 */
function nextTestAction(input) {
    if (input.stage !== 'assessment')
        return null;
    if (input.offline)
        return nextExamAction(input, input.offline);
    if (input.latestStatus === null)
        return 'send';
    if (exports.OPEN_ATTEMPT_STATUSES.includes(input.latestStatus))
        return null;
    if (input.latestStatus === 'expired' || input.latestStatus === 'cancelled') {
        return input.attemptsUsed < input.attemptsAllowed ? 'resend' : null;
    }
    if (input.latestDidPass === false && input.attemptsUsed < input.attemptsAllowed)
        return 'retake';
    return null;
}
function nextExamAction(input, offline) {
    if (input.latestStatus === null)
        return 'book';
    if (exports.OPEN_ATTEMPT_STATUSES.includes(input.latestStatus))
        return null;
    if (input.attemptsUsed >= input.attemptsAllowed)
        return null;
    if (UNSAT_STATUSES.includes(input.latestStatus))
        return offline.missedSessions <= offline.noShowChances ? 'book' : null;
    return input.latestDidPass === false ? 'book' : null;
}
/** F9.03 by hand: a first link, a fresh link, or a retake — the server works out which. */
exports.sendTestSchema = zod_1.z.object({ version: zod_1.z.number().int().nonnegative() });
// --- The candidate's side (ASM-03) ---------------------------------------------------
/** The header a test page sends its link's token in — kept out of addresses and logs. */
exports.TEST_TOKEN_HEADER = 'x-test-token';
exports.saveTestAnswerSchema = zod_1.z.object({ answer: exports.testAnswerSchema });
// --- Marking written answers (ASM-06, F9.07) ------------------------------------------------
/** §L22 N-24: straight away, then again after a day; N-25 at three days, to the grader and HR Lead. */
exports.GRADING_REMINDER_HOURS = 24;
exports.GRADING_OVERDUE_HOURS = 72;
/**
 * How many of the two chasers (N-24 again, then N-25) should have gone by now. The sweep
 * records the count before sending, so a chaser is never sent twice.
 */
function gradingChasersDue(submittedAt, now) {
    const hours = (now.getTime() - submittedAt.getTime()) / 3_600_000;
    return [exports.GRADING_REMINDER_HOURS, exports.GRADING_OVERDUE_HOURS].filter((mark) => hours >= mark).length;
}
exports.GRADER_NOTE_MAX = 2000;
/**
 * One answer's mark, to two places; a person may give part marks where the system never
 * does. Not versioned: answers are marked one after another at speed, each is its own
 * row, and the server holds the attempt while it adds them up.
 */
exports.markTestAnswerSchema = zod_1.z.object({
    points: schemas_1.twoDecimalMarkSchema,
    note: zod_1.z.string().trim().max(exports.GRADER_NOTE_MAX).nullable(),
});
function markProblem(points, maxPoints) {
    if (points < 0)
        return 'A mark cannot be below 0.';
    if (points > maxPoints)
        return `This question is worth ${maxPoints}.`;
    return null;
}
//# sourceMappingURL=assessments.js.map